/**
 * TaskFloatingCommentInput
 *
 * Page-level floating comment input. Lives outside the Comments tab so it
 * stays visible while the user browses History or Campaigns tabs.
 *
 * Collapsed: renders a pill at the bottom-center of the viewport.
 * Expanded: opens the full toolbar + textarea + attachments editor.
 */

import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Input, Tag, Button, Tooltip, message } from 'antd';
import {
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  LoadingOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  MessageOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { CopyBlock } from 'react-code-blocks';

import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { handleUploadFilesToS3 } from '../../utils/task.utils';
import FileDropPasteArea from '../../utils/FileDropPasteArea';
import { allowedFileExtensions, allowedImageExtensions } from '../../configs/task.config';

const TaskFloatingCommentInput = ({ currentTaskId, onCommentPosted, formRef }) => {
  const [form] = Form.useForm();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Expose the internal form to parents that need to read draft state
  // (e.g. drawer's unsaved-comment guard).
  useEffect(() => {
    if (formRef) {
      formRef.current = form;
    }
  }, [form, formRef]);

  const { currentUser, createComment, refetchTask } = useTaskContext();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [inputExpanded, setInputExpanded] = useState(false);

  const expandInput = () => {
    setInputExpanded(true);
    setTimeout(() => {
      const textarea = textareaRef.current?.resizableTextArea?.textArea;
      textarea?.focus();
    }, 0);
  };

  const collapseInput = () => {
    setInputExpanded(false);
  };

  const handleUploadFileClick = () => fileInputRef.current.click();
  const handleUploadImageClick = () => imageInputRef.current.click();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedFileExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Images are not allowed in this field.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedFiles: [...(form.getFieldValue('attachedFiles') || []), ...files],
    });
    e.target.value = null;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedImageExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Only image files are allowed.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedImages: [...(form.getFieldValue('attachedImages') || []), ...files],
    });
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const attachedFiles = form.getFieldValue('attachedFiles') || [];
    attachedFiles.splice(index, 1);
    form.setFieldsValue({ attachedFiles });
  };

  const handleRemoveImage = (index) => {
    const attachedImages = form.getFieldValue('attachedImages') || [];
    attachedImages.splice(index, 1);
    form.setFieldsValue({ attachedImages });
  };

  const handleInsertCode = () => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('comment') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;
    if (selectedText) {
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    form.setFieldsValue({ comment: newValue });
    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const insertText = (options) => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;
    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);
    form.setFieldsValue({ comment: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = textareaRef.current?.resizableTextArea?.textArea;
      if (!textarea) return;
      const value = form.getFieldValue('comment') || '';
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3;
        } else {
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ comment: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleSubmitComment = async (values) => {
    const { comment, attachedFiles, attachedImages, attachedCodeSnippet } = values;
    let mediaImages = [];
    let mediaFiles = [];

    if (
      (!comment || !comment.trim()) &&
      (!attachedFiles || !attachedFiles.length) &&
      (!attachedImages || !attachedImages.length) &&
      (!attachedCodeSnippet || !attachedCodeSnippet.trim())
    ) {
      message.destroy();
      message.error('Comment or an attachment is required.');
      return;
    }

    try {
      setButtonDisabled(true);
      message.loading('Posting comment...', 0);

      if (attachedImages && attachedImages.length) {
        mediaImages = await handleUploadFilesToS3(attachedImages);
      }
      if (attachedFiles && attachedFiles.length) {
        mediaFiles = await handleUploadFilesToS3(attachedFiles);
      }

      const commentData = {
        comment: comment || '',
        attachedImages: mediaImages.length ? { create: mediaImages } : {},
        attachedFiles: mediaFiles.length ? { create: mediaFiles } : {},
        codeSnippet: attachedCodeSnippet || '',
        createdBy: { connect: { id: currentUser.id } },
        task: { connect: { id: currentTaskId } },
      };

      await createComment({ variables: { data: commentData } });

      form.resetFields();
      await refetchTask();

      if (onCommentPosted) onCommentPosted();

      message.destroy();
      message.success('Comment posted successfully!');
      setButtonDisabled(false);
      setInputExpanded(false);
    } catch (error) {
      message.destroy();
      message.error(`Failed to post comment: ${error.message}`);
      setButtonDisabled(false);
    }
  };

  const onButtonClick = () => form.submit();

  if (!currentTaskId) return null;

  return (
    <Form form={form} onFinish={handleSubmitComment} layout="vertical">
      <div
        className={`task-floating-input${
          inputExpanded
            ? ' task-floating-input--expanded'
            : ' task-floating-input--collapsed'
        }`}
      >
        {!inputExpanded && (
          <button type="button" className="task-comment__input-pill" onClick={expandInput}>
            <MessageOutlined className="task-comment__input-pill-icon" />
            <span className="task-comment__input-pill-text">Write a comment…</span>
          </button>
        )}
        {inputExpanded && (
          <>
            <Row className="task-comment__input-container">
              <FileDropPasteArea
                onImagesAdded={(newImages) => {
                  const currentImages = form.getFieldValue('attachedImages') || [];
                  form.setFieldsValue({ attachedImages: [...currentImages, ...newImages] });
                }}
                onFilesAdded={(newFiles) => {
                  const currentFiles = form.getFieldValue('attachedFiles') || [];
                  form.setFieldsValue({ attachedFiles: [...currentFiles, ...newFiles] });
                }}
              >
                <div className="task-comment__toolbar">
                  <Tooltip title="Bold" mouseEnterDelay={0.5}>
                    <Button
                      icon={<BoldOutlined />}
                      onClick={() => insertText({ before: '**', after: '**' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Italic" mouseEnterDelay={0.5}>
                    <Button
                      icon={<ItalicOutlined />}
                      onClick={() => insertText({ before: '*', after: '*' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                    <Button
                      icon={<StrikethroughOutlined />}
                      onClick={() => insertText({ before: '~~', after: '~~' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<OrderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return { text: '1. ', newStart: start + 3, newEnd: start + 3 };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const numbered = lines.map((line, i) => `${i + 1}. ${line}`).join('\n');
                            return { text: numbered, newStart: start, newEnd: start + numbered.length };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<UnorderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return { text: '- ', newStart: start + 2, newEnd: start + 2 };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const dashed = lines.map((line) => `- ${line}`).join('\n');
                            return { text: dashed, newStart: start, newEnd: start + dashed.length };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                </div>
                <Form.Item noStyle shouldUpdate>
                  {(formInstance) => {
                    const attachedFiles = formInstance.getFieldValue('attachedFiles') || [];
                    const attachedImages = formInstance.getFieldValue('attachedImages') || [];

                    return attachedFiles.length > 0 || attachedImages.length > 0 ? (
                      <>
                        <div className="task-comment__attachment-preview">
                          {attachedFiles.length > 0 && (
                            <Row className="task-comment__attachment-row">
                              {attachedFiles.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveFile(index)}
                                  icon={<PaperClipOutlined />}
                                  className="task-comment__attachment-tag"
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}
                          {attachedImages.length > 0 && (
                            <Row className="task-comment__attachment-row">
                              {attachedImages.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveImage(index)}
                                  icon={<PictureOutlined />}
                                  className="task-comment__attachment-tag"
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}
                        </div>
                        <Row style={{ flexGrow: 1 }}>
                          <Form.Item name="comment" noStyle>
                            <Input.TextArea
                              ref={textareaRef}
                              onKeyDown={handleKeyDown}
                              onChange={(e) => {
                                form.setFieldsValue({ comment: e.target.value });
                                formInstance.setFieldsValue({ comment: e.target.value });
                              }}
                              autoSize={{ minRows: 6, maxRows: 12 }}
                              maxLength={3000}
                              placeholder="Enter task details"
                              className="task-comment__textarea task-comment__textarea--with-attachments"
                            />
                          </Form.Item>
                        </Row>
                      </>
                    ) : (
                      <Row style={{ flexGrow: 1 }}>
                        <Form.Item name="comment" noStyle>
                          <Input.TextArea
                            className="custom-textarea task-comment__textarea"
                            onKeyDown={handleKeyDown}
                            ref={textareaRef}
                            onChange={(e) => {
                              form.setFieldsValue({ comment: e.target.value });
                              formInstance.setFieldsValue({ comment: e.target.value });
                            }}
                            maxLength={3000}
                            autoSize={{ minRows: 6, maxRows: 12 }}
                            placeholder="Add a comment"
                          />
                        </Form.Item>
                      </Row>
                    );
                  }}
                </Form.Item>

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedFileExtensions.join(',')}
                  onChange={handleFileChange}
                />
                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedImageExtensions.join(',')}
                  onChange={handleImageChange}
                />
              </FileDropPasteArea>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.isCodeSnippet !== currentValues.isCodeSnippet ||
                  prevValues.commentsCodeValue !== currentValues.commentsCodeValue
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('isCodeSnippet') &&
                  getFieldValue('commentsCodeValue') && (
                    <Col className="task-comment__code-snippet">
                      <CopyBlock
                        text={getFieldValue('commentsCodeValue')}
                        language="javascript"
                        wrapLongLines
                        showLineNumbers={false}
                        theme="dracula"
                        codeBlock
                      />
                    </Col>
                  )
                }
              </Form.Item>
            </Row>

            <Row className="task-comment__actions-row">
              <Col className="task-comment__upload-buttons">
                <Form.Item name="attachedFiles" noStyle>
                  <Tooltip title="Upload file" mouseEnterDelay={0.3}>
                    <Button className="task-comment__upload-btn" onClick={handleUploadFileClick}>
                      <PaperClipOutlined />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Form.Item name="attachedImages" noStyle>
                  <Tooltip title="Upload image" mouseEnterDelay={0.3}>
                    <Button className="task-comment__upload-btn" onClick={handleUploadImageClick}>
                      <PictureOutlined />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Tooltip title="Code block" mouseEnterDelay={0.3}>
                  <Button className="task-comment__upload-btn" onClick={handleInsertCode}>
                    <CodeOutlined />
                  </Button>
                </Tooltip>
              </Col>
              <Col className="task-comment__submit-area">
                <Button
                  onClick={collapseInput}
                  disabled={buttonDisabled}
                  className="task-comment__cancel"
                  icon={<MinusOutlined />}
                >
                  Minimize
                </Button>
                <Button
                  onClick={onButtonClick}
                  disabled={buttonDisabled}
                  className="task-comment__submit"
                >
                  {buttonDisabled && <LoadingOutlined />} Submit
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Form>
  );
};

TaskFloatingCommentInput.propTypes = {
  currentTaskId: PropTypes.string,
  onCommentPosted: PropTypes.func,
  formRef: PropTypes.object,
};

TaskFloatingCommentInput.defaultProps = {
  currentTaskId: null,
  onCommentPosted: null,
  formRef: null,
};

// Memoize so parent state churn (assignee/details/status changes) doesn't
// re-render the editor while the user is typing — the draft is held inside
// the antd Form instance, but minimizing re-renders also keeps cursor/IME
// state stable.
export default memo(TaskFloatingCommentInput);
